import React, { useContext, useEffect, useMemo, useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import LocContext from "../context/locContext";
import { Link } from "react-router-dom";
import "./overview.css";

const FUND_TABS = [
  "Equity Schemes",
  "Debt Schemes",
  "Hybrid Schemes",
  "Other Schemes",
  "Solution Oriented Schemes",
];

const FUNDS_BY_TAB = {
  "Equity Schemes": [
    { name: "Nippon India Growth Mid Cap Fund", category: "Mid Cap", returns: "18.4% (3Y)", initials: "NI" },
    { name: "Nippon India Small Cap Fund", category: "Small Cap", returns: "21.2% (3Y)", initials: "NI" },
    { name: "Parag Parikh Flexi Cap Fund", category: "Flexi Cap", returns: "16.8% (3Y)", initials: "PP" },
    { name: "HDFC Mid-Cap Opportunities", category: "Mid Cap", returns: "17.1% (3Y)", initials: "HD" },
  ],
  "Debt Schemes": [
    { name: "ICICI Pru Corporate Bond Fund", category: "Corporate Bond", returns: "7.2% (3Y)", initials: "IC" },
    { name: "HDFC Short Term Debt Fund", category: "Short Duration", returns: "6.8% (3Y)", initials: "HD" },
    { name: "SBI Magnum Gilt Fund", category: "Gilt", returns: "7.5% (3Y)", initials: "SB" },
    { name: "Axis Banking & PSU Debt", category: "Banking & PSU", returns: "6.9% (3Y)", initials: "AX" },
  ],
  "Hybrid Schemes": [
    { name: "HDFC Balanced Advantage", category: "Dynamic Asset", returns: "14.2% (3Y)", initials: "HD" },
    { name: "ICICI Pru Equity & Debt", category: "Aggressive Hybrid", returns: "15.1% (3Y)", initials: "IC" },
    { name: "SBI Equity Hybrid Fund", category: "Aggressive Hybrid", returns: "13.8% (3Y)", initials: "SB" },
    { name: "Kotak Equity Hybrid Fund", category: "Aggressive Hybrid", returns: "14.0% (3Y)", initials: "KO" },
  ],
  "Other Schemes": [
    { name: "Nippon India Gold Savings", category: "FoFs", returns: "12.4% (3Y)", initials: "NI" },
    { name: "ICICI Pru Bharat 22 FOF", category: "FoFs", returns: "15.6% (3Y)", initials: "IC" },
    { name: "Motilal Oswal Nasdaq 100", category: "International", returns: "18.9% (3Y)", initials: "MO" },
    { name: "Mirae Asset NYSE FANG+", category: "International", returns: "19.5% (3Y)", initials: "MA" },
  ],
  "Solution Oriented Schemes": [
    { name: "HDFC Retirement Savings", category: "Retirement", returns: "13.2% (3Y)", initials: "HD" },
    { name: "SBI Magnum Children's Benefit", category: "Children", returns: "12.1% (3Y)", initials: "SB" },
    { name: "ICICI Pru Retirement Fund", category: "Retirement", returns: "12.8% (3Y)", initials: "IC" },
    { name: "Tata Retirement Savings", category: "Retirement", returns: "13.0% (3Y)", initials: "TA" },
  ],
};

const ACTIONS = [
  { label: "Create New Folio", icon: "fa-folder-plus", to: "/my-folios" },
  { label: "Download Statement", icon: "fa-file-arrow-down", to: "/reports/account-statement" },
  { label: "OTBM registration", icon: "fa-building-columns", to: "/mandatesmf" },
  { label: "Redeem", icon: "fa-money-bill-transfer", to: "/redeemMf" },
  { label: "Switch", icon: "fa-right-left", to: "/switchmf" },
  { label: "Show More", icon: "fa-ellipsis", to: "/explore", isMore: true },
];

export default function OverView() {
  const { userData, refreshUserData } = useContext(LocContext);
  const [bannerOpen, setBannerOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(FUND_TABS[0]);

  const investorOnboardingComplete = Number(userData?.mutual_status) === 1;

  useEffect(() => {
    if (refreshUserData) refreshUserData();
  }, [refreshUserData]);

  const fullName = useMemo(() => {
    const parts = [
      userData?.first_name,
      userData?.middle_name,
      userData?.last_name,
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "Client";
  }, [userData]);

  const initial = (userData?.first_name || "C").charAt(0).toUpperCase();

  const formatINR = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const portfolio = {
    currentValue: Number(userData?.portfolio_value) || 0,
    invested: Number(userData?.invested_amount) || 0,
    returns: Number(userData?.total_returns) || 0,
  };

  const funds = FUNDS_BY_TAB[activeTab] || FUNDS_BY_TAB["Equity Schemes"];

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f3f6f9" }}>
        <div className="ov-page">
          {bannerOpen && (
            <div className="ov-banner">
              <div className="ov-banner-icon" aria-hidden>
                <i className="fa-solid fa-bullhorn" />
              </div>
              <p>
                Markets are dynamic. Stay invested for long term to create wealth.
                Need help? Talk to your advisor today!
              </p>
              <button
                type="button"
                className="ov-banner-close"
                aria-label="Dismiss banner"
                onClick={() => setBannerOpen(false)}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          )}

          <div className="ov-welcome">
            <div className="ov-welcome-avatar" aria-hidden>
              {userData?.profile_image ? (
                <img src={userData.profile_image} alt="" />
              ) : (
                initial
              )}
            </div>
            <h1>
              Welcome, {fullName}
              <button
                type="button"
                className="ov-welcome-refresh"
                title="Refresh"
                aria-label="Refresh"
                onClick={() => refreshUserData && refreshUserData()}
              >
                <i className="fa-solid fa-arrow-right" />
              </button>
            </h1>
          </div>

          {!investorOnboardingComplete && (
            <div className="ov-alert warn">
              <div>
                <h6>Your investor profile is incomplete</h6>
                <p>
                  Complete mutual fund investor onboarding to unlock investing.
                  This is a one-time step with the exchange.
                </p>
              </div>
              <Link to="/investor-onboarding" className="ov-alert-btn">
                Complete onboarding
              </Link>
            </div>
          )}

          {investorOnboardingComplete && (
            <div className="ov-alert ok">
              <div>
                <h6>Registration received</h6>
                <p>
                  Check your email for next steps. Exchange authentication may still
                  be pending — you can explore funds meanwhile.
                </p>
              </div>
            </div>
          )}

          <div className="ov-grid">
            <div className="ov-card ov-portfolio">
              <div className="ov-metrics">
                <div>
                  <p className="ov-metric-label">Current Value</p>
                  <p className="ov-metric-value">{formatINR(portfolio.currentValue)}</p>
                </div>
                <div>
                  <p className="ov-metric-label">Invested Amount</p>
                  <p className="ov-metric-value">{formatINR(portfolio.invested)}</p>
                </div>
                <div>
                  <p className="ov-metric-label">Returns</p>
                  <p className="ov-metric-value">{formatINR(portfolio.returns)}</p>
                </div>
              </div>

              <div className="ov-actions">
                {ACTIONS.map((action) => (
                  <Link
                    key={action.label}
                    to={action.to}
                    className={`ov-action${action.isMore ? " ov-action-more" : ""}`}
                  >
                    <div className="ov-action-icon">
                      <i className={`fa-solid ${action.icon}`} />
                    </div>
                    <span>{action.label}</span>
                    {action.isMore && (
                      <span className="ov-more-arrow" aria-hidden>
                        <i className="fa-solid fa-arrow-right" />
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            <div className="ov-side-stack">
              <div className="ov-card ov-sip-card">
                <div style={{ flex: 1 }}>
                  <p>
                    Markets evolve. So should your portfolio. Restart or begin a SIP
                    to stay on track.
                  </p>
                  <Link to="/sipinvest" className="ov-btn-teal">
                    Start a SIP Now
                  </Link>
                </div>
                <div className="ov-sip-art" aria-hidden>
                  <i className="fa-solid fa-seedling" />
                </div>
              </div>

              <div className="ov-card ov-get-started">
                <div className="ov-rocket" aria-hidden>
                  <div className="ov-rocket-body">
                    <div className="ov-rocket-window" />
                    <div className="ov-rocket-fin left" />
                    <div className="ov-rocket-fin right" />
                  </div>
                  <div className="ov-rocket-flame" />
                </div>

                <h2>Get Started!</h2>
                <p className="ov-gs-sub">Build your own path for journey.</p>
                <p className="ov-gs-body">
                  Whether you&apos;re looking to grow your wealth, save for a goal,
                  or explore new opportunities — start your investment journey today.
                </p>
                <div className="ov-gs-hint">
                  <i className="fa-solid fa-coins" aria-hidden />
                  <span>Start an investing plan with as low as ₹ 100 per month</span>
                </div>
                <div className="ov-gs-actions">
                  <Link to="/sipinvest" className="ov-btn-outline">
                    START AN SIP
                  </Link>
                  <Link to="/invest-online-bse/new-investment" className="ov-btn-solid">
                    INVEST NOW
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <section className="ov-funds">
            <div className="ov-funds-head">
              <h3>Top Performing Funds</h3>
              <div className="ov-funds-art" aria-hidden>
                <i className="fa-solid fa-bullseye" />
              </div>
            </div>

            <div className="ov-tabs" role="tablist">
              {FUND_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`ov-tab${activeTab === tab ? " active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="ov-fund-grid">
              {funds.map((fund) => (
                <Link
                  key={fund.name}
                  to="/explore"
                  className="ov-fund-card"
                >
                  <div className="ov-fund-logo">{fund.initials}</div>
                  <h4>{fund.name}</h4>
                  <p className="ov-fund-meta">{fund.category}</p>
                  <p className="ov-fund-return">{fund.returns}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
